import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { getMembershipDiscountLineItem, getPurchaseItemsHavingMembershipCodeDiscount } from "common";
import React from "react";
import { useMutation } from "react-query";
import { LineItemObject, Purchase, PurchaseItem } from "types";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import type { PurchasePayload } from "@/components/PurchaseModification/EditPurchaseCodesModal";

interface RemoveMembershipCodeModalProps extends ModalContentProps {
    isOpen: boolean;
}

export const RemoveMembershipCodeModal = ({ isOpen, onClose, ...rest }: RemoveMembershipCodeModalProps) => {
    return (
        /** Don't pass `onClose` as a prop directly, as the callback depends on `updatedPurchase` or `undefined`.
         * Using the prop as `onClose={onClose}` would pass the SyntheticEvent as `updatedPurchase` and changes the behaviour */
        <Modal isOpen={isOpen} onClose={() => onClose()}>
            <ModalContent onClose={onClose} {...rest} />
        </Modal>
    );
};

interface ModalContentProps {
    purchase: Purchase;
    item?: PurchaseItem;
    onClose: (updatedPurchase?: Purchase) => void;
}

const ModalContent = ({ onClose, purchase, item }: ModalContentProps) => {
    const items = item ? [item] : getPurchaseItemsHavingMembershipCodeDiscount(purchase);

    const {
        mutateAsync,
        isLoading: isRemoving,
        isSuccess,
    } = useMutation(async (payload: PurchasePayload) => {
        try {
            const { data: updatedPurchase } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
            return updatedPurchase;
        } catch {
            flash.danger("Error removing membership code, you may remove it from membership purchase details");
        }
    });

    const handleRemoveCode = async () => {
        const payload: PurchasePayload = { ops: [] };

        for (const item of items) {
            const membershipCodeItem = getMembershipDiscountLineItem(item)!;

            payload.ops?.push({
                path: `/items/${item.id}/lineItems`,
                op: "removeLineItem",
                data: {
                    id: membershipCodeItem.id,
                    object: LineItemObject.DISCOUNT_CODE,
                },
            });
        }

        const updatedPurchase = await mutateAsync(payload);
        onClose(updatedPurchase);
    };

    return (
        <>
            <Modal.Header>Membership Usage</Modal.Header>

            <Modal.Body>
                <p className="mb-2">
                    There is a membership code associated with this reservation. If you leave this code associated to
                    the reservation, it will continue to count against the usage limit of the membership. If you remove
                    this code, then it will give 1 membership usage back to the member.
                </p>
                <p>Would you like to remove the membership code from this reservation?</p>
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                <SecondaryButton disabled={isRemoving} onClick={() => onClose()}>
                    Keep Code
                </SecondaryButton>

                <SubmitButton
                    isLoading={isRemoving}
                    isSuccess={isSuccess}
                    disabled={!items?.length}
                    onClick={handleRemoveCode}
                >
                    Remove Code
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};
